/**
 * LMIV-Allergene + Diät-Flags — EINE gemeinsame Quelle für alle Anzeige-Flächen
 * (Katalog, Menü-Import, Pakete, Angebots-Builder, öffentliches Angebot).
 *
 * Rechtlicher Rahmen (LMIV / § 4 LMIDV): Allergen-Information muss überall dort verfügbar
 * sein, wo Speisen angeboten werden. Deshalb gilt UI-weit die Tri-State-Semantik aus dem
 * Katalog: `null` = ungepflegt (sichtbar als „–", NIE als „keine" darstellen!),
 * `[]` = geprüft: keine Hauptallergene, Codes = geprüfte Allergene.
 *
 * H-02-Rest (Migration 336): `[]` allein reicht nicht mehr — Alt-Importe (vor WELLE 2a/H-02,
 * 11.07.2026) schrieben `[]` als reinen Default, OHNE dass ein Mensch „keine Allergene"
 * bestätigt hat. `catalog_items.allergens_checked_at` unterscheidet das jetzt: `checked=false`
 * bei `[]` markiert „ungeprüfter Altbestand" — visuell wie `null` behandelt (NIE als „keine"
 * ausgeben), aber mit eigenem Tooltip. `checked` fehlt/​ist `true` → normales Verhalten
 * (Default `true`: bestehende Aufrufer ohne den dritten Zustand ändern sich nicht).
 */

/** 14 LMIV-Hauptallergene (Buchstaben-Kennzeichnung wie in DE üblich), DE + EN Label. */
export const LMIV: { code: string; label: string; labelEn: string }[] = [
  { code: "A", label: "Glutenhaltiges Getreide", labelEn: "Cereals containing gluten" },
  { code: "B", label: "Krebstiere", labelEn: "Crustaceans" },
  { code: "C", label: "Eier", labelEn: "Eggs" },
  { code: "D", label: "Fisch", labelEn: "Fish" },
  { code: "E", label: "Erdnüsse", labelEn: "Peanuts" },
  { code: "F", label: "Soja", labelEn: "Soybeans" },
  { code: "G", label: "Milch / Laktose", labelEn: "Milk / lactose" },
  { code: "H", label: "Schalenfrüchte", labelEn: "Tree nuts" },
  { code: "L", label: "Sellerie", labelEn: "Celery" },
  { code: "M", label: "Senf", labelEn: "Mustard" },
  { code: "N", label: "Sesam", labelEn: "Sesame" },
  { code: "O", label: "Schwefeldioxid / Sulfite", labelEn: "Sulphur dioxide / sulphites" },
  { code: "P", label: "Lupinen", labelEn: "Lupin" },
  { code: "R", label: "Weichtiere", labelEn: "Molluscs" },
];

const LMIV_BY_CODE = new Map(LMIV.map((l) => [l.code, l]));

/** Voller Name zu einem Code (Fallback: der Code selbst, falls unbekannte Alt-Daten). */
export function lmivLabel(code: string, lang: "de" | "en" = "de"): string {
  const entry = LMIV_BY_CODE.get(code);
  if (!entry) return code;
  return lang === "en" ? entry.labelEn : entry.label;
}

/**
 * Kurz-Labels der LMIV-Allergen-Codes für kompakte Chat-Kachel-Anzeigen (Assistent/Copilot).
 * Bewusst knapper als {@link lmivLabel} (z. B. „Gluten" statt „Glutenhaltiges Getreide"),
 * damit die Chips im Gesprächsfaden nicht umbrechen. EINE gemeinsame Quelle für alle Agent-Kacheln.
 */
export const ALLERGEN_LABEL: Record<string, string> = {
  A: "Gluten", B: "Krebstiere", C: "Eier", D: "Fisch", E: "Erdnüsse", F: "Soja", G: "Milch/Laktose",
  H: "Schalenfrüchte", L: "Sellerie", M: "Senf", N: "Sesam", O: "Sulfite", P: "Lupinen", R: "Weichtiere",
};

/** Diät-/Unverträglichkeits-Flags (identisch zur Katalog-Pflege). */
export const DIETARY: { key: string; label: string; labelEn: string; short: string }[] = [
  { key: "vegetarian", label: "Vegetarisch", labelEn: "Vegetarian", short: "Veg" },
  { key: "vegan", label: "Vegan", labelEn: "Vegan", short: "Vegan" },
  { key: "gluten_free", label: "Glutenfrei", labelEn: "Gluten-free", short: "GF" },
  { key: "lactose_free", label: "Laktosefrei", labelEn: "Lactose-free", short: "LF" },
];

/** Aktive Diät-Flags eines Items als Liste (stabile Reihenfolge wie DIETARY). */
export function activeDietary(dietary: Record<string, boolean> | null | undefined): typeof DIETARY {
  if (!dietary) return [];
  return DIETARY.filter((d) => dietary[d.key] === true);
}

/**
 * Allergen-Codes + Diät-Flags als kompakte, read-only Badge-Zeile.
 * Tri-State: `allergens === null` -> „–" (ungepflegt, Tooltip erklärt), `[]` -> „keine",
 * Codes -> Buchstaben-Chips mit vollem Namen im Tooltip.
 *
 * H-02-Rest (Migration 336): vierter sichtbarer Fall (technisch weiter Tri-State der DATEN,
 * aber ein zusätzlicher UI-Zustand) — `allergens === []` UND `checked === false` ist ungeprüfter
 * Alt-Bestand: dieselbe optische Behandlung wie „ungepflegt" (grau, „–"), aber ein eigener,
 * genauerer Tooltip. `checked` default `true` — Aufrufer, die den Pruef-Zeitpunkt nicht kennen
 * (kein Zugriff auf `catalog_items.allergens_checked_at`), bekommen unverändert das alte
 * Verhalten (populierte Codes sind davon ohnehin nie betroffen, s. `aggregateAllergens`).
 */
export function AllergenBadges({
  allergens,
  checked = true,
  dietary,
  className = "",
}: {
  allergens: string[] | null | undefined;
  /** false NUR bedeutungsvoll bei allergens=[] — siehe Kopfkommentar. */
  checked?: boolean;
  dietary?: Record<string, boolean> | null;
  className?: string;
}) {
  const diet = activeDietary(dietary);
  const ungepruefterAltbestand = allergens != null && allergens.length === 0 && checked === false;
  return (
    <span className={`inline-flex flex-wrap items-center gap-1 ${className}`}>
      {allergens == null || ungepruefterAltbestand ? (
        <span
          className="text-[11.5px] text-[#9A9080] dark:text-on-surface-variant/60"
          title={
            ungepruefterAltbestand
              ? "Allergene ungeprüft – Alt-Bestand aus einem Import vor der Einzel-Bestätigung. Bitte im Katalog prüfen und bestätigen (LMIV)."
              : "Allergene ungepflegt – bitte im Katalog prüfen (LMIV)"
          }
        >
          –
        </span>
      ) : allergens.length === 0 ? (
        /*
         * Punkt 75, Schritt 2 (Entwurf 41a, Befund ④ „Kennzeichen uneinheitlich — liest sich wie
         * ein Fehler"): der Zustand hiess „keine" und war gruen.
         *
         * Zwei Aenderungen, und beide haben denselben Grund. **„keine" allein sagt nicht, wovon**
         * — in einer Zeile, in der daneben Mengen, Preise und Diaetangaben stehen, ist das eine
         * offene Frage. Jetzt steht dort, was gemeint ist. Und **die Farbe trug die halbe
         * Aussage**: gruen hiess „geprueft", grau hiess „ungeprueft". Wer die Farben nicht
         * unterscheidet, las beide Zustaende gleich. Der Unterschied steckt jetzt im WORT, wo er
         * hingehoert; die Farbe ist neutral wie bei den Buchstaben-Chips daneben.
         *
         * Die LMIV-Unterscheidung selbst bleibt unangetastet: „keine Allergene" wird weiterhin
         * NUR bei einem echten Pruef-Zeitpunkt gezeigt, sonst steht dort das gedaempfte „–".
         */
        <span className="text-[11.5px] text-[#6F675A] dark:text-secondary-fixed-dim" title="Geprüft: keine LMIV-Hauptallergene">
          Keine Allergene
        </span>
      ) : (
        [...allergens].sort().map((a) => (
          <span
            key={a}
            /* Entwurf 41a (Punkt 75, Schritt 3): 18 px hoch, 5 px Radius, 10,5 px/600, der
               gedaempfte Sandton der Palette. Derselbe Zuschnitt wie der Positionszaehler im
               Sektionskopf — ein Chip sieht ueberall gleich aus. */
            className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-[5px] bg-[#F0EAE0] px-[6px] text-[10.5px] font-semibold text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]"
            title={lmivLabel(a)}
          >
            {a}
          </span>
        ))
      )}
      {diet.map((d) => (
        <span
          key={d.key}
          className="inline-flex h-[18px] items-center justify-center rounded-[5px] bg-[#F0EAE0] px-[6px] text-[10.5px] font-semibold text-[#6F675A] dark:bg-primary-fixed-dim/15 dark:text-primary-fixed-dim"
          title={d.label}
        >
          {d.short}
        </span>
      ))}
    </span>
  );
}

/**
 * Union der Allergene über mehrere Positionen (z. B. alle Gänge eines Menü-Pakets).
 * LMIV-Kernregel: Schon EINE ungepflegte Position (`null`) macht das Aggregat unvollständig —
 * dann darf NIE „keine Allergene" behauptet werden (`resolved: false`).
 *
 * H-02-Rest (Migration 336): eine leere Position OHNE echten Pruef-Zeitpunkt (`checked: false`)
 * zaehlt genauso wie `null` — ein ungeprueft leerer Alt-Bestand darf das Aggregat nicht
 * „vervollstaendigen".
 */
export function aggregateAllergens(
  items: { allergens: string[] | null | undefined; checked?: boolean }[],
): { union: string[]; resolved: boolean } {
  const set = new Set<string>();
  let resolved = true;
  for (const it of items) {
    if (it.allergens == null || (it.allergens.length === 0 && it.checked === false)) {
      resolved = false;
      continue;
    }
    for (const a of it.allergens) set.add(a);
  }
  return { union: [...set].sort(), resolved };
}
